/**
 * Centralized pricing configuration.
 * All plan pricing, credit allocations, and tier metadata are defined here.
 */

export const PRICING = {
  starter: {
    monthly: 5,
    annual: 48,
    credits: 10,
    label: 'Starter',
  },
  pro: {
    monthly: 15,
    annual: 144,
    credits: 40,
    label: 'Pro',
  },
  creditPack: {
    price: 3.5,
    credits: 5,
  },
  annualDiscount: 0.2,
} as const;

/** Tier ordering for upgrade/downgrade comparison. */
export const TIER_ORDER: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
};

/** Capitalize the first letter of a string. */
export const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

/** Get the formatted price string for a tier and billing period. */
export const getTierPrice = (
  tier: 'starter' | 'pro',
  billingPeriod: 'monthly' | 'annual'
): string => {
  const config = PRICING[tier];
  if (billingPeriod === 'annual') {
    return `$${config.annual}/year`;
  }
  return `$${config.monthly}/month`;
};

/** Get the per-month price for a tier and billing period. */
export const getMonthlyPrice = (
  tier: 'starter' | 'pro',
  billingPeriod: 'monthly' | 'annual'
): number => {
  const config = PRICING[tier];
  if (billingPeriod === 'annual') {
    return config.monthly * (1 - PRICING.annualDiscount);
  }
  return config.monthly;
};

/** Get annual savings amount for a tier. */
export const getAnnualSavings = (tier: 'starter' | 'pro'): number =>
  Math.round(PRICING[tier].monthly * 12 * PRICING.annualDiscount);

/** Get credits for a tier. */
export const getCreditsForTier = (tier: string): number => {
  if (tier === 'pro') return PRICING.pro.credits;
  if (tier === 'starter') return PRICING.starter.credits;
  return 0;
};
